import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { expertService } from '../../api';
import Header from '../Header/Header';
import Footer from '../footer/Footer';
import { 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaGlobeEurope,
  FaMoneyBillWave,
  FaEuroSign,
  FaLinkedin,
  FaCalendarAlt,
  FaCheckCircle,
  FaFlag,
  FaUserTie,
  FaTools,
  FaHandsHelping,
  FaArrowLeft,
  FaCommentAlt,
  FaExternalLinkAlt,
  FaWhatsapp
} from 'react-icons/fa';
import './DetailsExpert.css';

const DetailsExpert = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expert, setExpert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('infos');
  const [showContactOptions, setShowContactOptions] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    fetchExpertDetails();
  }, [id]);

  const fetchExpertDetails = async () => {
    try {
      setLoading(true);
      setImageError(false);
      
      const expertData = await expertService.getById(id);
      
      if (!expertData) {
        throw new Error('Expert non trouvé');
      }
      
      // Construire l'URL de l'image si elle existe
      const expertWithImage = { ...expertData };
      
      if (expertData.image || expertData.image_url) {
        const imagePath = expertData.image || expertData.image_url;
        expertWithImage.imageUrl = getImageUrl(imagePath);
      }
      
      setExpert(expertWithImage);
    } catch (err) {
      console.error('Erreur détail expert:', err);
      setError(err.message || 'Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour construire l'URL de l'image
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    const baseUrl = process.env.REACT_APP_API_URL || 'https://plateforme.etef-design.fr';
    
    // Si c'est déjà une URL complète
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Nettoyer le chemin si nécessaire
    const cleanedPath = imagePath.startsWith('storage/') 
      ? imagePath.replace('storage/', '') 
      : imagePath;
    
    return `${baseUrl}/storage/${cleanedPath}`;
  };

  // Fonction pour obtenir l'URL à afficher
  const getDisplayImageUrl = () => {
    if (imageError) {
      return null; // Retourner null pour afficher l'avatar par défaut
    }
    
    if (expert?.imageUrl) {
      return expert.imageUrl;
    }
    
    // Fallback
    if (expert?.image || expert?.image_url) {
      return getImageUrl(expert.image || expert.image_url);
    }
    
    return null;
  };

  const formatPrice = (price) => {
    if (!price) return 'Non spécifié';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Fonction pour ouvrir WhatsApp
  const handleContactWhatsApp = () => {
    if (!expert?.telephone) {
      navigate('/message', { 
        state: { 
          error: "Cet expert n'a pas de numéro de téléphone disponible",
          type: 'warning'
        }
      });
      return;
    }

    // Nettoyer le numéro de téléphone
    const phoneNumber = expert.telephone.replace(/\s+/g, '').replace(/[^\d+]/g, '');
    
    // Formater le numéro pour WhatsApp
    let whatsappNumber = phoneNumber;
    if (!whatsappNumber.startsWith('+')) {
      if (whatsappNumber.startsWith('0')) {
        whatsappNumber = '+33' + whatsappNumber.substring(1);
      } else {
        whatsappNumber = '+33' + whatsappNumber;
      }
    }

    // Message par défaut
    const defaultMessage = `Bonjour ${expert.name || 'Monsieur/Madame'}, je vous contacte via votre profil sur la plateforme étudiante.`;
    
    // Construire l'URL WhatsApp
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(defaultMessage)}`;
    
    // Ouvrir WhatsApp
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  // Fonction pour appeler
  const handleContactPhone = () => {
    if (!expert?.telephone) {
      navigate('/message', { 
        state: { 
          error: "Cet expert n'a pas de numéro de téléphone disponible",
          type: 'warning'
        }
      });
      return;
    }
    
    // Utiliser navigate pour une page intermédiaire si besoin, ou ouvrir directement
    const phoneUrl = `tel:${expert.telephone}`;
    window.location.href = phoneUrl;
  };

  // Fonction pour envoyer un email
  const handleContactEmail = () => {
    if (!expert?.email) {
      navigate('/message', { 
        state: { 
          error: "Cet expert n'a pas d'email disponible",
          type: 'warning'
        }
      });
      return;
    }
    
    const subject = `Contact depuis la plateforme étudiante`;
    const body = `Bonjour ${expert.name || 'Monsieur/Madame'},\n\nJe vous contacte via votre profil sur la plateforme étudiante.`;
    
    const mailtoUrl = `mailto:${expert.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  // Fonction pour voir le profil LinkedIn
  const handleLinkedIn = () => {
    if (expert?.linkedin) {
      window.open(expert.linkedin, '_blank', 'noopener,noreferrer');
    }
  };

  // Fonction pour fermer le dropdown en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showContactOptions && !event.target.closest('.contact-dropdown-container')) {
        setShowContactOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showContactOptions]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement des informations de l'expert...</p>
      </div>
    );
  }

  if (error || !expert) {
    return (
      <div className="error-container">
        <h2><FaUserTie /> Expert non trouvé</h2>
        <p>{error || "L'expert que vous recherchez n'existe pas ou a été supprimé."}</p>
        <button onClick={() => navigate('/experts')} className="back-button">
          <FaArrowLeft /> Retour à la liste des experts
        </button>
      </div>
    );
  }

  const displayImageUrl = getDisplayImageUrl();

  return (
    <div className="details-expert-containerbigcontain">
      <Header />
      <div className="details-expert-container">
        
        <div className="breadcrumb">
          <button onClick={() => navigate('/experts')} className="breadcrumb-link">
            <FaArrowLeft /> Retour à la liste des experts
          </button>
        </div>

        <div className="expert-header">
          <div className="expert-profile-image">
            {displayImageUrl ? (
              <img 
                src={displayImageUrl} 
                alt={`${expert.name} ${expert.prenom}`} 
                onError={() => {
                  console.error(`Échec chargement image expert ${expert.id}: ${displayImageUrl}`);
                  setImageError(true);
                }}
                onLoad={() => console.log(`Image chargée pour expert ${expert.id}: ${displayImageUrl}`)}
              />
            ) : (
              <div className="no-profile-image">
                {expert.name?.charAt(0)}{expert.prenom?.charAt(0)}
              </div>
            )}
          </div>
          
          <div className="expert-header-info">
            <div className="expert-title">
              <h1><FaUserTie className="title-icon" /> {expert.name} {expert.prenom}</h1>
              <p className="expert-subtitle">Expert professionnel</p>
            </div>
            
            <div className="expert-tags">
              <span className="tag expert">
                <FaUserTie /> Expert
              </span>
              <span className="tag location">
                <FaMapMarkerAlt /> {expert.ville}
              </span>
              <span className="tag price">
                <FaMoneyBillWave /> {expert.tarif || 'Tarif non spécifié'}
              </span>
              {expert.prix && (
                <span className="tag amount">
                  <FaEuroSign /> {formatPrice(expert.prix)}
                </span>
              )}
            </div>
            
            <div className="expert-actions">
              <div className="contact-dropdown-container">
                <button 
                  className="btn-contact" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowContactOptions(!showContactOptions);
                  }}
                >
                  <FaCommentAlt /> Contacter l'expert
                </button>
                
                {showContactOptions && (
                  <div className="contact-dropdown">
                    {/* Option WhatsApp */}
                    {expert.telephone && (
                      <button 
                        className="dropdown-item whatsapp" 
                        onClick={handleContactWhatsApp}
                      >
                        <FaWhatsapp /> WhatsApp
                      </button>
                    )}
                    
                    {/* Option Téléphone */}
                    {expert.telephone && (
                      <button 
                        className="dropdown-item phone" 
                        onClick={handleContactPhone}
                      >
                        <FaPhone /> Téléphoner
                      </button>
                    )}
                    
                    {/* Option Email */}
                    {expert.email && (
                      <button 
                        className="dropdown-item email" 
                        onClick={handleContactEmail}
                      >
                        <FaEnvelope /> Email
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {expert.linkedin && (
                <button className="btn-linkedin" onClick={handleLinkedIn}>
                  <FaLinkedin /> LinkedIn
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bouton WhatsApp flottant (mobile) */}
        {expert.telephone && (
          <div className="floating-whatsapp-button" onClick={handleContactWhatsApp}>
            <FaWhatsapp />
          </div>
        )}

        <div className="expert-tabs">
          <button 
            className={`tab-btn ${activeTab === 'infos' ? 'active' : ''}`}
            onClick={() => setActiveTab('infos')}
          >
            <FaUserTie /> Informations
          </button>
          <button 
            className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            <FaTools /> Services
          </button>
          <button 
            className={`tab-btn ${activeTab === 'accompagnement' ? 'active' : ''}`}
            onClick={() => setActiveTab('accompagnement')}
          >
            <FaHandsHelping /> Accompagnement
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'infos' && (
            <div className="info-section">
              <div className="section">
                <h3><FaUserTie /> Présentation</h3>
                <div className="bio-card">
                  <p className="bio">{expert.bio || "Aucune biographie disponible."}</p>
                </div>
              </div>

              <div className="contact-details">
                <h3><FaEnvelope /> Coordonnées</h3>
                <div className="contact-grid">
                  {expert.email && (
                    <div className="contact-item clickable" onClick={handleContactEmail}>
                      <div className="contact-icon">
                        <FaEnvelope />
                      </div>
                      <div className="contact-info">
                        <span className="label">Email</span>
                        <span className="value">{expert.email}</span>
                        <small className="click-hint">Cliquez pour envoyer un email</small>
                      </div>
                    </div>
                  )}
                  {expert.telephone && (
                    <div className="contact-item clickable" onClick={handleContactPhone}>
                      <div className="contact-icon">
                        <FaPhone />
                      </div>
                      <div className="contact-info">
                        <span className="label">Téléphone</span>
                        <span className="value">{expert.telephone}</span>
                        <small className="click-hint">Cliquez pour appeler</small>
                      </div>
                    </div>
                  )}
                  {expert.ville && (
                    <div className="contact-item">
                      <div className="contact-icon">
                        <FaMapMarkerAlt />
                      </div>
                      <div className="contact-info">
                        <span className="label">Ville</span>
                        <span className="value">{expert.ville}</span>
                      </div>
                    </div>
                  )}
                  {expert.region_couverte && (
                    <div className="contact-item">
                      <div className="contact-icon">
                        <FaGlobeEurope />
                      </div>
                      <div className="contact-info">
                        <span className="label">Région couverte</span>
                        <span className="value">{expert.region_couverte}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Boutons d'action rapides */}
                <div className="quick-actions">
                  {expert.telephone && (
                    <button className="quick-action-btn whatsapp-btn" onClick={handleContactWhatsApp}>
                      <FaWhatsapp /> WhatsApp
                    </button>
                  )}
                </div>
              </div>

              <div className="tarif-section">
                <h3><FaMoneyBillWave /> Informations tarifaires</h3>
                <div className="tarif-card">
                  <div className="tarif-item">
                    <div className="tarif-icon">
                      <FaMoneyBillWave />
                    </div>
                    <div className="tarif-info">
                      <span className="label">Type de tarif</span>
                      <span className="value">{expert.tarif || 'Non spécifié'}</span>
                    </div>
                  </div>
                  {expert.prix && (
                    <div className="tarif-item">
                      <div className="tarif-icon">
                        <FaEuroSign />
                      </div>
                      <div className="tarif-info">
                        <span className="label">Prix</span>
                        <span className="value price">{formatPrice(expert.prix)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="services-section">
              <h3><FaTools /> Services proposés</h3>
              <div className="services-card">
                {expert.services ? (
                  <div className="services-list">
                    {(Array.isArray(expert.services) 
                      ? expert.services 
                      : (typeof expert.services === 'string' 
                        ? JSON.parse(expert.services) 
                        : [])
                    ).map((service, index) => (
                      <div key={index} className="service-item">
                        <div className="service-icon">
                          <FaTools />
                        </div>
                        <span className="service-text">{service}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">Aucun service spécifié</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'accompagnement' && (
            <div className="accompagnement-section">
              <h3><FaHandsHelping /> Modes d'accompagnement</h3>
              <div className="modes-card">
                {expert.modes_accompagnement ? (
                  <div className="modes-list">
                    {(Array.isArray(expert.modes_accompagnement)
                      ? expert.modes_accompagnement
                      : (typeof expert.modes_accompagnement === 'string'
                        ? JSON.parse(expert.modes_accompagnement)
                        : [])
                    ).map((mode, index) => (
                      <div key={index} className="mode-item">
                        <div className="mode-icon">
                          <FaCheckCircle />
                        </div>
                        <span className="mode-text">{mode}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">Aucun mode d'accompagnement spécifié</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="expert-footer">
          <div className="meta-info">
            <span className="meta-item">
              <FaCalendarAlt />
              Membre depuis : {new Date(expert.created_at).toLocaleDateString('fr-FR')}
            </span>
            {expert.email_verified_at && (
              <span className="meta-item verified">
                <FaCheckCircle />
                Email vérifié
              </span>
            )}
          </div>
          
          <button className="btn-report" onClick={() => navigate('/contact', { state: { expertId: expert.id } })}>
            <FaFlag /> Signaler un problème
          </button>
        </div>
        
      </div>
      <Footer />
    </div>
  );
};

export default DetailsExpert;